<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import { tutors } from '@/routes/index'
import { Card } from '@/Components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs'
import { Home, ChevronRight } from 'lucide-vue-next'

// Import tutor components
import TutorHero from '@/Components/Tutors/TutorHero.vue'
import IntroVideo from '@/Components/Tutors/IntroVideo.vue'
import AvailabilityCalendar from '@/Components/Tutors/AvailabilityCalendar.vue'
import ContactTutor from '@/Components/Tutors/ContactTutor.vue'
import ShareProfile from '@/Components/Tutors/ShareProfile.vue'
import TrustBadges from '@/Components/Tutors/TrustBadges.vue'
import MembershipPlansCard from '@/Components/Tutors/MembershipPlansCard.vue'
import OverviewTab from '@/Components/Tutors/OverviewTab.vue'
import PricingPlans from '@/Components/Tutors/PricingPlans.vue'
import ContentLibrary from '@/Components/Tutors/ContentLibrary.vue'
import ReviewsTab from '@/Components/Tutors/ReviewsTab.vue'

interface Tutor {
  id: number
  slug: string
  first_name: string
  last_name: string
  name: string
  email: string
  phone: string
  avatar: string
  highlight: string
  rating: number
  reviews_count: number
  students_count: number
  videos_count: number
  subjects: Array<{ id: number; name: string; slug: string; color: string; icon: string }>
  levels: Array<{ id: number; name: string }>
  examboards: Array<{ id: number; name: string }>
  about_me?: string
  academic_qualifications: any[]
  certifications: any[]
  services_offered: string[]
  price_base_tier: number
  price_silver_tier: number
  price_gold_tier: number
  group_session_availability: any[]
  one_on_one_availability: any[]
  teaching_experience?: string
  teaching_style?: string
  cv_path?: string
  intro_video?: string | null
  group_subjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  locked?: boolean
}

interface LegendItem {
  label: string
  bg: string
}

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutor: Tutor
  availability: TimeSlot[][]
  availabilityLegend?: LegendItem[]
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  subscriptions: () => [],
})

const activeTab = ref('overview')

onMounted(() => {
  trackEvent('viewContent', {
    content_type: 'tutor_profile',
    content_id: props.tutor.id,
    content_name: props.tutor.name,
    tutor_name: props.tutor.name,
    subjects: props.tutor.subjects.map(s => s.name).join(', '),
  })
})
</script>

<template>
  <AppLayout>
    <div class="container mx-auto mt-10 px-0 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <section id="breadcrumb" class="mb-6">
        <div class="flex items-center gap-2 text-sm">
          <a :href="tutors().url" class="text-gray-500 hover:text-gray-900 flex items-center gap-1">
            <Home class="w-4 h-4" />
            Home
          </a>
          <ChevronRight class="w-4 h-4 text-gray-400" />
          <a :href="tutors().url" class="text-gray-500 hover:text-gray-900">Tutors</a>
          <ChevronRight class="w-4 h-4 text-gray-400" />
          <span class="text-gray-900 font-medium">{{ tutor.name }}</span>
        </div>
      </section>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Hero Section -->
          <TutorHero :tutor="tutor" />

          <!-- Introduction Video -->
          <IntroVideo
            v-if="tutor.intro_video"
            :tutor-name="tutor.first_name"
            :video-title="`Meet ${tutor.first_name} - Your Tutor`"
            :playback-id="tutor.intro_video"
          />

          <!-- Tabs Section -->
          <Card class="border-0 shadow-sm max-sm:rounded-none">
            <Tabs v-model="activeTab" class="w-full">
              <TabsList class="w-full justify-between md:justify-start border-0 border-b border-gray-200 rounded-none bg-transparent p-0 px-6 h-auto">
                <TabsTrigger
                  value="overview"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="membership"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                    <span class="md:hidden">Plans</span>
                    <span class="hidden md:block">Membership Plans</span>
                </TabsTrigger>
                <TabsTrigger
                  value="content"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >

                    <span class="md:hidden">Content</span>
                    <span class="hidden md:block">Content Library</span>
                </TabsTrigger>
                <TabsTrigger
                  value="reviews"
                  class="rounded-none border-0 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 px-4 py-3"
                >
                  Reviews
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" class="p-4 md:p-6">
                <OverviewTab :tutor="tutor" :availability="availability" :legend="availabilityLegend" :subscriptions="subscriptions" />
              </TabsContent>

              <TabsContent value="membership" class="p-4 md:p-6">
                <PricingPlans
                  :tutor-slug="tutor.slug"
                  :tutor-id="tutor.id"
                  :base-price="tutor.price_base_tier"
                  :silver-price="tutor.price_silver_tier"
                  :gold-price="tutor.price_gold_tier"
                  :services-offered="tutor.services_offered"
                  :videos-count="tutor.videos_count"
                  :subjects="tutor.subjects"
                  :group-subjects="tutor.group_subjects"
                  :subscriptions="subscriptions"
                />
              </TabsContent>

              <TabsContent value="content" class="p-4 md:p-6">
                <ContentLibrary :tutor-slug="tutor.slug" />
              </TabsContent>

              <TabsContent value="reviews" class="p-4 md:p-6">
                <ReviewsTab />
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1">
          <div class="space-y-6">
            <!-- Membership Plans Card -->
            <MembershipPlansCard
              class="hidden lg:block"
              :tutor-slug="tutor.slug"
              :tutor-id="tutor.id"
              :tutor-name="tutor.first_name"
              :base-price="tutor.price_base_tier"
              :silver-price="tutor.price_silver_tier"
              :gold-price="tutor.price_gold_tier"
              :videos-count="tutor.videos_count"
              :services-offered="tutor.services_offered"
              :group-subjects="tutor.group_subjects"
              :subscriptions="subscriptions"
            />

            <!-- Contact Tutor -->
            <ContactTutor :tutor-name="tutor.first_name" />

            <!-- Share Profile -->
            <ShareProfile />

            <!-- Trust Badges -->
            <TrustBadges />
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
